import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, LucideIcon } from "lucide-react";
import {
  Code,
  Smartphone,
  Globe,
  Database,
  Cloud,
  Shield,
  Settings,
  Headphones,
  Cpu,
  BarChart3,
  Palette,
} from "lucide-react";

interface Service {
  icon: LucideIcon;
  title: string;
  category: string;
  description: string;
  features: string[];
  technologies: string[];
  href: string;
}

interface ServicesMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const ServicesMenu = ({ isOpen, onClose }: ServicesMenuProps) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      // Add event listener when dropdown opens
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      // Clean up event listeners
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Group services by category with shorter names for better display
  const serviceCategories = {
    Development: [
      {
        icon: Code,
        title: "Custom Software",
        category: "Development",
        description:
          "Build tailored software solutions that perfectly match your business requirements and workflows.",
        features: [
          "Custom Web Applications",
          "Enterprise Software",
          "Legacy System Modernization",
          "API Development",
        ],
        technologies: [
          "React",
          "Node.js",
          "Python",
          "Java",
          ".NET",
          "PostgreSQL",
        ],
        href: "/services/custom-software",
      },
      {
        icon: Globe,
        title: "Web Development",
        category: "Web",
        description:
          "Develop responsive, fast, and user-friendly websites and web applications.",
        features: [
          "Responsive Design",
          "E-commerce Solutions",
          "Progressive Web Apps",
          "Content Management Systems",
        ],
        technologies: [
          "React",
          "Vue.js",
          "Angular",
          "Next.js",
          "WordPress",
          "Shopify",
        ],
        href: "/services/web-development",
      },
      {
        icon: Smartphone,
        title: "Mobile Development",
        category: "Mobile",
        description:
          "Create engaging mobile applications for iOS and Android platforms with native performance.",
        features: [
          "Native iOS Apps",
          "Android Applications",
          "Cross-platform Development",
          "App Store Optimization",
        ],
        technologies: ["React Native", "Flutter", "Swift", "Kotlin", "Xamarin"],
        href: "/services/mobile-development",
      },
    ],
    Cloud: [
      {
        icon: Cloud,
        title: "Cloud Migration",
        category: "Cloud",
        description:
          "Seamlessly migrate your infrastructure to cloud platforms for improved scalability and cost efficiency.",
        features: [
          "Infrastructure Assessment",
          "Migration Strategy",
          "Data Migration",
          "Cloud Optimization",
        ],
        technologies: [
          "AWS",
          "Azure",
          "Google Cloud",
          "Docker",
          "Kubernetes",
          "Terraform",
        ],
        href: "/services/cloud-migration",
      },
      {
        icon: Settings,
        title: "DevOps",
        category: "DevOps",
        description:
          "Streamline your development and deployment processes with modern DevOps practices.",
        features: [
          "CI/CD Pipelines",
          "Infrastructure as Code",
          "Monitoring & Logging",
          "Automated Testing",
        ],
        technologies: [
          "Jenkins",
          "GitLab CI",
          "Docker",
          "Kubernetes",
          "Ansible",
          "Prometheus",
        ],
        href: "/services/devops",
      },
      {
        icon: Database,
        title: "Database Management",
        category: "Data",
        description:
          "Optimize and manage your databases for maximum performance and reliability.",
        features: [
          "Database Design",
          "Performance Tuning",
          "Data Migration",
          "Backup Solutions",
        ],
        technologies: [
          "PostgreSQL",
          "MongoDB",
          "MySQL",
          "Redis",
          "Elasticsearch",
          "Cassandra",
        ],
        href: "/services/database-management",
      },
    ],
    Analytics: [
      {
        icon: Cpu,
        title: "AI & Machine Learning",
        category: "AI/ML",
        description:
          "Implement intelligent solutions using cutting-edge AI and machine learning technologies.",
        features: [
          "Natural Language Processing",
          "Computer Vision",
          "Predictive Modeling",
          "Recommendation Systems",
        ],
        technologies: [
          "TensorFlow",
          "PyTorch",
          "OpenAI",
          "Scikit-learn",
          "Hugging Face",
        ],
        href: "/services/ai-ml",
      },
      {
        icon: BarChart3,
        title: "Data Analytics",
        category: "Analytics",
        description:
          "Transform your data into actionable insights with advanced analytics and business intelligence.",
        features: [
          "Data Visualization",
          "Predictive Analytics",
          "Real-time Dashboards",
          "Report Automation",
        ],
        technologies: [
          "Tableau",
          "Power BI",
          "Python",
          "R",
          "Apache Spark",
          "Snowflake",
        ],
        href: "/services/data-analytics",
      },
    ],
    Security: [
      {
        icon: Shield,
        title: "Cybersecurity",
        category: "Security",
        description:
          "Protect your digital assets with comprehensive security solutions and assessments.",
        features: [
          "Security Audits",
          "Penetration Testing",
          "Compliance Consulting",
          "Incident Response",
        ],
        technologies: ["OWASP", "ISO 27001", "SOC 2", "GDPR", "PCI DSS"],
        href: "/services/cybersecurity",
      },
    ],
    // Design: [
    //   {
    //     icon: Palette,
    //     title: "UI/UX Design",
    //     category: "Design",
    //     description:
    //       "Create intuitive and beautiful user experiences that delight your customers.",
    //     features: [
    //       "User Research",
    //       "Wireframing",
    //       "Prototyping",
    //       "Design Systems",
    //     ],
    //     technologies: ["Figma", "Adobe XD", "Sketch", "InVision", "Principle"],
    //     href: "/services/ui-ux-design",
    //   },
    // ],
    // Support: [
    //   {
    //     icon: Headphones,
    //     title: "24/7 Support",
    //     category: "Support",
    //     description:
    //       "Ensure your systems run smoothly with round-the-clock support and maintenance.",
    //     features: [
    //       "System Monitoring",
    //       "Bug Fixes",
    //       "Performance Optimization",
    //       "Security Updates",
    //     ],
    //     technologies: [
    //       "Monitoring Tools",
    //       "Ticketing Systems",
    //       "Remote Support",
    //       "Maintenance Protocols",
    //     ],
    //     href: "/services/support",
    //   },
    // ],
  };

  const handleServiceClick = (service: Service) => {
    navigate(service.href);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed top-14 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border shadow-xl z-[55]"
      onMouseEnter={() => {}}
      onMouseLeave={onClose}
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-foreground mb-1">
            Our Services
          </h3>
        </div>

        {/* Mobile Layout */}
        <div className="block md:hidden">
          <div className="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto">
            {Object.entries(serviceCategories).map(
              ([categoryName, services], index) => (
                <div
                  key={categoryName}
                  className="bg-background/50 rounded-lg p-4 border border-border/30"
                >
                  {/* Category Header */}
                  <div className="mb-3">
                    <h4
                      className={`text-sm font-bold ${index % 2 === 0 ? "text-primary" : "text-secondary"} uppercase tracking-wider`}
                    >
                      {categoryName}
                    </h4>
                  </div>

                  {/* Services List */}
                  <div className="space-y-2 flex-1">
                    {services.map((service, index) => {
                      const Icon = service.icon;

                      return (
                        <button
                          key={index}
                          onClick={() => handleServiceClick(service)}
                          className={`w-full group text-left px-3 py-3 text-sm text-foreground ${index % 2 === 0 ? 'hover:text-primary hover:bg-primary/5' : 'hover:text-secondary hover:bg-secondary/5'} transition-all duration-200 rounded-md border border-transparent ${index % 2 === 0 ? 'hover:border-primary/20' : 'hover:border-secondary/20'} flex items-center gap-3`}
                        >
                          <div
                            className={`flex h-8 w-8 items-center justify-center rounded-md ${index % 2 === 0 ? "bg-primary/10" : "bg-secondary/10"} flex-shrink-0`}
                          >
                            <Icon
                              className={`h-4 w-4 ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary"}`}
                            />
                          </div>

                          <div className={`font-medium text-sm leading-tight ${index % 2 === 0 ? 'group-hover:text-primary' : 'group-hover:text-secondary'} transition-colors`}>
                            {service.title}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(serviceCategories).map(([categoryName, services], index) => (
            <div key={categoryName} className="h-full flex flex-col">
              {/* Category Header */}
              <div className="mb-4 px-3">
                <h4
                  className={`text-sm font-bold ${index % 2 === 0 ? "text-primary" : "text-secondary"} uppercase tracking-wider`}
                >
                  {categoryName}
                </h4>
              </div>

              {/* Services List */}
              <div className="space-y-2 flex-1">
                {services.map((service, index) => {
                  const Icon = service.icon;

                  return (
                    <button
                      key={index}
                      onClick={() => handleServiceClick(service)}
                      className={`w-full group text-left px-3 py-3 text-sm text-foreground ${index % 2 === 0 ? 'hover:text-primary hover:bg-primary/5' : 'hover:text-secondary hover:bg-secondary/5'} transition-all duration-200 rounded-md border border-transparent ${index % 2 === 0 ? 'hover:border-primary/20' : 'hover:border-secondary/20'} flex items-center gap-3`}
                    >
                      {/* Icon */}
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-md ${index % 2 === 0 ? "bg-primary/10" : "bg-secondary/10"} flex-shrink-0`}
                      >
                        <Icon
                          className={`h-4 w-4 ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary"}`}
                        />
                      </div>

                      {/* Title */}
                      <div className={`font-medium text-sm leading-tight ${index % 2 === 0 ? 'group-hover:text-primary' : 'group-hover:text-secondary'} transition-colors`}>
                        {service.title}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServicesMenu;
